import React, { useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { useRange } from 'react-instantsearch';
import DoubleRangeSlider from '../../../../components/double-range-slider/DoubleRangeSlider';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '../../../../utils/sessionStorage';
import { updateSearchRefFromUrl } from '../utils';
import { postAlgoliaFilterClick } from '../../../../utils/algolia/search-insights';

const DEBOUNCE_TIME = 400;

export type PriceRangeTrackingInfo = {
  position: number;
  defaults: {
    sortBy: string;
    hitsPerPage: number;
  };
};

interface PriceRangeSliderProps {
  setCurrentRefinement: any;
  nbHits: number;
  shouldShowDollar: boolean;
  shouldShowDropdown: boolean;
  trackingInfo: PriceRangeTrackingInfo;
  attribute: string;
  defaultRefinement: any;
}

const PriceRangeSlider = ({
  setCurrentRefinement,
  nbHits,
  shouldShowDollar,
  shouldShowDropdown,
  trackingInfo,
  attribute,
}: PriceRangeSliderProps) => {
  const { range, start, refine } = useRange({ attribute });
  const { min, max }: { min: any; max: any } = range;
  const startMin: any = Number.isFinite(start[0]) ? start[0] : min;
  const startMax: any = Number.isFinite(start[1]) ? start[1] : max;
  const currentRefinement = {
    min: Math.max(min, startMin),
    max: Math.min(max, startMax),
  };
  const [debouncedState, setDebouncedState] = useState(null);
  const searchState = qs.parse(window.location.search.slice(1)) as any;
  const searchRef = useRef(searchState);
  const [prevMin, setPrevMin] = useState(min);
  const [prevMax, setPrevMax] = useState(max);
  const [priceMethod, setPriceMethod] = useState('text');

  const handleSetPriceMethod = (method: 'text' | 'number') => {
    setPriceMethod(method);
  };

  useEffect(() => {
    setCurrentRefinement({
      ...currentRefinement,
      isRefined: currentRefinement && (currentRefinement.min !== min || currentRefinement.max !== max),
    });
  }, [currentRefinement.min, currentRefinement.max]);

  useEffect(() => {
    if (min && max && currentRefinement.min && currentRefinement.max) {
      if (currentRefinement.max > max || currentRefinement.max < min) {
        refine([currentRefinement.min, max]);
      } else if (currentRefinement.min > max || currentRefinement.min < min) {
        refine([min, currentRefinement.max]);
      }
    }
  }, [min, max, currentRefinement.min, currentRefinement.max]);

  const onRefine = ({ currentMin, currentMax }: { currentMin: number; currentMax: number }) => {
    clearTimeout(debouncedState);

    setDebouncedState(
      setTimeout(() => {
        const minChanged = min !== currentMin;
        const maxChanged = max !== currentMax;
        refine([currentMin, currentMax]);

        if (minChanged && prevMin !== currentMin) {
          pushToDataLayer({
            event: 'select_filter',
            filter_group: 'Price',
            filter_name: 'min price',
            filter_type: priceMethod,
            filter_value: currentMin,
            search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
            search_term: searchState?.query || '',
          });
          postAlgoliaFilterClick('price', `>=${currentMin}`);
          setPrevMin(currentMin);
        }
        if (maxChanged && prevMax !== currentMax) {
          pushToDataLayer({
            event: 'select_filter',
            filter_group: 'Price',
            filter_name: 'max price',
            filter_type: priceMethod,
            filter_value: currentMax,
            search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
            search_term: searchState?.query || '',
          });
          postAlgoliaFilterClick('price', `<=${currentMax}`);
          setPrevMax(currentMax);
        }

        const filters = Object.keys(searchState)
          .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
          .reduce((obj: any, key: string) => {
            const keyLowerCase = key.toLowerCase() as keyof typeof searchState;
            if (keyLowerCase === 'price') {
              obj[keyLowerCase] = { type: 'range', value: searchState[key] };
            } else {
              obj[keyLowerCase] = Array.isArray(searchState[key])
                ? searchState[key].map((i: any) => ({ type: 'checkbox', value: i }))
                : { type: 'checkbox', value: searchState[key] };
            }
            return obj;
          }, {});

        updateSearchRefFromUrl(searchRef);

        window.dataLayer.push({
          event: 'custom.filter.set',
          group: 'site search',
          category: 'search',
          label: min === currentMin && max === currentMax ? 'off' : 'on',
          name: 'price',
          value: {
            min: currentMin,
            max: currentMax,
          },
          info: {
            element: 'slider',
            position: trackingInfo.position,
            sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
            hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
          },
          newFilters: {
            ...filters,
            price: {
              type: 'range',
              value: {
                min: currentMin,
                max: currentMax,
              },
            },
          },
          oldFilters: filters,
        });
      }, DEBOUNCE_TIME) as any
    );
  };

  /**
   * Render function
   */
  return (
    <DoubleRangeSlider
      onSetPriceMethod={handleSetPriceMethod}
      showDollar={shouldShowDollar}
      nbHits={nbHits}
      min={min}
      max={max}
      currentMin={currentRefinement.min}
      currentMax={currentRefinement.max || 0}
      onRefine={onRefine}
      showDropdown={shouldShowDropdown}
    />
  );
};

export default PriceRangeSlider;
